// HU-31 · Parametrizar las reglas de negocio. F-07, Sprint 2.
//
// El administrador ajusta, sin redesplegar, las dos reglas de evaluación que más pesan y
// que todavía se están decidiendo: con qué algoritmo se clasifica la causalidad (RN-14 /
// RN-18) y qué hace grave a un evento (RN-03).
//
// TRES BLOQUES, UNA REGLA COMÚN: LO YA DECIDIDO NO SE REESCRIBE.
//   1. Catálogo de algoritmos — agregar uno nuevo (C1). Entra como «propuesto».
//   2. Algoritmo predeterminado — cuál llega preseleccionado (C2). Las evaluaciones ya
//      guardadas conservan el suyo (C3): cada una copió código y versión al clasificar.
//   3. Criterio de gravedad — las condiciones, versionadas (C4). Cambiarlo no reclasifica
//      lo ya decidido (C5): cada decisión copió los criterios con los que se tomó.
//   4. Plazo interno del Comité (HU-23-C8, RN-19) — las horas hábiles que tiene el Comité
//      para el FAR-03. Es un parámetro global (no por autoridad); la bandeja lo aplica al
//      instante y cada FAR-03 conserva el plazo con el que se elaboró.
//   5. Estado del Comité de cada algoritmo (propuesto / por evaluar / por definir /
//      vigente) — es INFORMACIÓN del Comité, no una regla: no cambia qué algoritmo llega
//      preseleccionado (eso es el bloque 2) ni retira el algoritmo. Se cambia por fila,
//      con motivo, y el backend rechaza el estado que ya tiene.
//
// Cada guardado exige MOTIVO. No es burocracia: HU-31 dice que "cada caso conserva con
// qué versión de la regla se resolvió", y el motivo es lo que permite explicar en una
// inspección por qué la regla cambió ese día.
//
// SOLO EL ADMINISTRADOR (C7). Lo decide `modulos_permitidos` de `GET /sesion` para el módulo entero y lo vuelve a
// decidir el backend con el token en los dos routers. Esta pantalla no añade una tercera
// guarda: si llega alguien sin rol, el 403 se muestra tal cual.

import { useCallback, useEffect, useRef, useState } from 'react';
import { FlaskConical, Pencil, Plus, Scale, ShieldOff, Star, Timer } from 'lucide-react';
import {
  Alert, Button, Card, DataTable, EmptyState, Field, Input, Modal, PageHeader, Select,
  StatusBadge, Textarea,
} from '../components/ui';
import * as api from '../api/reglas';
import {
  CLAVE_CRITERIO_GRAVEDAD, CLAVE_PLAZO_INTERNO, CLAVE_PREDETERMINADO, ESTADOS_DE_ALGORITMO, ESTADOS_DEL_COMITE,
} from '../api/reglas';
import { etiquetaDelCriterio } from '../api/evaluacion';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import type { Algoritmo } from '../api/reglas';
import type { RespuestaDelApi } from '../tipos';
import ParametrosDelSistema from './ParametrosDelSistema';
// RN-82: el plazo interno es UN valor para todos los países; sin país, su vigencia se lee en la zona
// por defecto (`null` = America/Lima, el mismo respaldo que el servidor).
import { comoFechaDeInstante } from '../lib/fechas';

// La respuesta del GET de parámetro trae la versión vigente; el `valor` viaja dentro.
const valorVigente = (respuesta: RespuestaDelApi | null): unknown => respuesta?.valor ?? null;

// Recorta los `_` de los dos bordes. Con índices y no con `/^_+|_+$/`: esa expresión
// retrocede sobre una racha larga de guiones bajos (Sonar la marca como ReDoS).
const sinGuionesBajosEnLosBordes = (texto: string): string => {
  let inicio = 0;
  let fin = texto.length;
  while (inicio < fin && texto[inicio] === '_') inicio += 1;
  while (fin > inicio && texto[fin - 1] === '_') fin -= 1;
  return texto.slice(inicio, fin);
};

// Un criterio se escribe en snake_case (es una clave, no un texto): "malformacion_congenita".
const aClaveDeCriterio = (texto: string): string =>
  sinGuionesBajosEnLosBordes(
    texto
      .trim()
      .toLowerCase()
      .normalize('NFD')
      // Los diacríticos por su rango Unicode escrito con escapes: el rango literal
      // «[̀-ͯ]» son dos caracteres combinantes invisibles que un editor puede normalizar
      // o perder sin que nadie lo note.
      .replaceAll(/[\u0300-\u036f]/g, '')
      .replaceAll(/[^a-z0-9]+/g, '_'),
  );

export default function ReglasDeNegocio() {
  const [algoritmos, setAlgoritmos] = useState<Algoritmo[]>([]);
  const [predeterminado, setPredeterminado] = useState<RespuestaDelApi | null>(null);
  const [criterio, setCriterio] = useState<RespuestaDelApi | null>(null);
  const [plazoInterno, setPlazoInterno] = useState<RespuestaDelApi | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [exito, setExito] = useState<string | null>(null);

  const [agregando, setAgregando] = useState(false);
  const [cambiandoPredeterminado, setCambiandoPredeterminado] = useState(false);
  const [editandoCriterio, setEditandoCriterio] = useState(false);
  const [editandoPlazo, setEditandoPlazo] = useState(false);
  // El CÓDIGO del algoritmo cuyo estado se está cambiando (null = modal cerrado). Se guarda
  // el código y no la fila: así el modal lee siempre la fila viva del catálogo, y una
  // recarga no lo deja mirando una copia vieja.
  const [codigoDelEstado, setCodigoDelEstado] = useState<string | null>(null);

  // `vivo`: una recarga que llega con la pantalla desmontada no escribe estado.
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const recargar = useCallback(async () => {
    setCargando(true);
    setFallo(null);
    try {
      const [lista, pred, crit, plazo] = await Promise.all([
        api.listarAlgoritmos(),
        api.verParametro(CLAVE_PREDETERMINADO),
        api.verParametro(CLAVE_CRITERIO_GRAVEDAD),
        api.verParametro(CLAVE_PLAZO_INTERNO),
      ]);
      if (!vivo.current) return;
      setAlgoritmos(lista);
      setPredeterminado(pred);
      setCriterio(crit);
      setPlazoInterno(plazo);
    } catch (e) {
      if (vivo.current) setFallo(aFallo(e));
    } finally {
      if (vivo.current) setCargando(false);
    }
  }, []);

  useEffect(() => { recargar(); }, [recargar]);

  // Al abrir cualquier modal, el aviso de éxito anterior se retira: «Algoritmo agregado»
  // encima de un formulario nuevo confunde sobre qué se acaba de guardar.
  // `estado` necesita además el código del algoritmo de la fila.
  const abrir = (cual: 'agregar' | 'predeterminado' | 'criterio' | 'plazo' | 'estado', codigo?: string) => {
    setExito(null);
    if (cual === 'agregar') setAgregando(true);
    else if (cual === 'predeterminado') setCambiandoPredeterminado(true);
    else if (cual === 'plazo') setEditandoPlazo(true);
    else if (cual === 'estado') setCodigoDelEstado(codigo ?? null);
    else setEditandoCriterio(true);
  };

  const codigoPredeterminado = String(valorVigente(predeterminado) ?? '');
  const algoritmoDelEstado = algoritmos.find((a) => a.codigo === codigoDelEstado) ?? null;
  const criterios = Array.isArray(valorVigente(criterio)) ? (valorVigente(criterio) as string[]) : [];
  // El plazo es un entero de horas hábiles; cualquier otra cosa se muestra como «sin definir».
  const horasDelPlazo = typeof valorVigente(plazoInterno) === 'number' ? (valorVigente(plazoInterno) as number) : null;

  const guardado = (mensaje: string) => {
    setExito(mensaje);
    recargar();
  };

  const encabezado = (
    <PageHeader
      eyebrow="Administración · Reglas de negocio"
      hu="HU-31 · AJ-EP · RN-14 / RN-18 / RN-03"
      title="Reglas de negocio"
      subtitle="Algoritmos de causalidad, criterio de gravedad y parámetros del sistema. Lo ya decidido conserva la regla con la que se decidió."
    />
  );

  // AJ-EP-C4 (MG-ILE-918): un rol distinto del administrador ve el texto común de Administración.
  if (fallo?.estado === 403) {
    return (
      <>
        {encabezado}
        <EmptyState icon={ShieldOff} title="No tienes acceso a esta opción" data-testid="reglas-sin-permiso"
          description="Las reglas de negocio las mantiene el administrador del sistema." />
      </>
    );
  }

  return (
    <>
      {encabezado}

      {fallo && <Alert tone="danger" title="No se pudo cargar" data-testid="reglas-error" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
      {exito && <Alert tone="success" data-testid="reglas-exito" onCerrar={() => setExito(null)}>{exito}</Alert>}

      {cargando && !fallo ? (
        <p data-testid="reglas-cargando" role="status" className="text-body text-ink-soft">Cargando reglas…</p>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {/* ---------------- 1 · Catálogo de algoritmos (C1) ---------------- */}
          <Card
            title="Catálogo de algoritmos de causalidad"
            icon={FlaskConical}
            // Bug 715 (ADO): QA puso un algoritmo en «Vigente» esperando que quedara
            // preseleccionado. El estado del Comité es información (bloque 5 de la cabecera);
            // lo que llega preseleccionado se cambia en el bloque 2. Se dice donde se mira.
            subtitle="El estado del Comité es informativo. El algoritmo preseleccionado se cambia más abajo."
            actions={
              <Button size="sm" icon={Plus} onClick={() => abrir('agregar')}>
                Agregar algoritmo
              </Button>
            }
          >
            {algoritmos.length === 0 ? (
              <EmptyState title="No hay algoritmos en el catálogo" description="Agrega el primero. Sin catálogo el Comité no puede clasificar la causalidad." />
            ) : (
              <DataTable
                data-testid="reglas-algoritmos"
                columns={[
                  { key: 'codigo', label: 'Código' },
                  { key: 'nombre', label: 'Nombre' },
                  { key: 'version', label: 'Versión' },
                  { key: 'estado', label: 'Estado del Comité' },
                  { key: 'predeterminado', label: 'Preseleccionado' },
                  { key: 'acciones', label: 'Acciones' },
                ]}
                rows={algoritmos as unknown as RespuestaDelApi[]}
                renderCell={(fila, columna) => {
                  if (columna.key === 'estado') {
                    return (
                      <StatusBadge tone={fila.estado === 'vigente' ? 'success' : 'neutral'}>
                        {ESTADOS_DE_ALGORITMO[String(fila.estado)] ?? String(fila.estado)}
                      </StatusBadge>
                    );
                  }
                  if (columna.key === 'predeterminado') {
                    return fila.codigo === codigoPredeterminado
                      ? <StatusBadge tone="ai" icon={Star}>Preseleccionado</StatusBadge>
                      : <span className="text-ink-soft text-body">—</span>;
                  }
                  if (columna.key === 'acciones') {
                    // El nombre accesible lleva algoritmo Y versión («Cambiar estado de Naranjo · v2»):
                    // el nombre no es único —recalibrar es agregar otra fila con el mismo nombre y
                    // otra versión—, así que se etiqueta como el preseleccionado (`nombre · vN`).
                    return (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={Pencil}
                        aria-label={`Cambiar estado de ${String(fila.nombre ?? fila.codigo)} · v${String(fila.version ?? '')}`}
                        onClick={() => abrir('estado', String(fila.codigo))}
                      >
                        Cambiar estado
                      </Button>
                    );
                  }
                  return String(fila[columna.key] ?? '');
                }}
              />
            )}
          </Card>

          {/* ---------------- 2 · Predeterminado (C2, C3) ---------------- */}
          <Card
            title="Algoritmo preseleccionado"
            icon={Star}
            actions={<Button size="sm" variant="secondary" onClick={() => abrir('predeterminado')}>Cambiar</Button>}
          >
            <p className="text-body">
              Las evaluaciones nuevas llegan con{' '}
              <strong>{algoritmos.find((a) => a.codigo === codigoPredeterminado)?.nombre ?? (codigoPredeterminado || 'ninguno')}</strong>{' '}
              preseleccionado, y el Comité puede cambiarlo en cada evaluación.
            </p>
            <p className="text-body text-ink-soft mt-2">
              Cambiarlo <strong>no toca las evaluaciones ya guardadas</strong>: cada una conserva el algoritmo y la versión con los que se clasificó.
            </p>
          </Card>

          {/* ---------------- 3 · Criterio de gravedad (C4, C5) ---------------- */}
          <Card
            title="Criterio de gravedad"
            icon={Scale}
            actions={<Button size="sm" variant="secondary" onClick={() => abrir('criterio')}>Ajustar</Button>}
          >
            <p className="text-body text-ink-soft mb-2">
              El Comité ve estas condiciones como guía al decidir la gravedad. Es grave el evento que cumple al menos una.
            </p>
            {criterios.length === 0 ? (
              <EmptyState title="No hay criterio configurado" description="El sistema no trae un criterio por defecto. Configúralo con «Ajustar»." />
            ) : (
              <ul className="list-disc pl-5 text-body space-y-1">
                {criterios.map((c) => <li key={c}>{etiquetaDelCriterio(c)}</li>)}
              </ul>
            )}
            <p className="text-body text-ink-soft mt-3">
              Cambiarlo <strong>no reclasifica lo ya decidido</strong>: cada decisión conserva registrados los criterios con los que se tomó.
            </p>
          </Card>

          {/* ---------------- 4 · Plazo interno del Comité (HU-23-C8, RN-19) ---------------- */}
          <Card
            title="Plazo interno del Comité"
            icon={Timer}
            actions={<Button size="sm" variant="secondary" onClick={() => abrir('plazo')}>Cambiar plazo</Button>}
          >
            <p className="text-body text-ink-soft mb-2">
              Horas hábiles que tiene el Comité para elaborar el FAR-03 desde que el caso entra a evaluación. Es un solo valor para todos los países (el plazo ante cada autoridad se configura aparte, en «Autoridades y plazos»).
            </p>
            <p className="text-body" aria-label="Plazo interno vigente">
              {horasDelPlazo === null
                ? <span className="italic text-ink-soft">sin definir (el sistema asume 24 h hábiles)</span>
                : <><strong>{horasDelPlazo} h hábiles</strong>{plazoInterno?.vigente_desde ? <span className="text-ink-soft"> · vigente desde {comoFechaDeInstante(plazoInterno.vigente_desde, null)}</span> : null}</>}
            </p>
            <p className="text-body text-ink-soft mt-3">
              Cada FAR-03 ya elaborado <strong>conserva el plazo</strong> con el que se midió.
            </p>
          </Card>

          {/* ---------------- 5 · Parámetros del sistema (AJ-EP, MG-ILE-918) ---------------- */}
          <ParametrosDelSistema onExito={(mensaje) => setExito(mensaje)} />
        </div>
      )}

      <ModalAgregarAlgoritmo
        abierto={agregando}
        onCerrar={() => setAgregando(false)}
        onGuardado={(a) => { setAgregando(false); guardado(`Algoritmo «${a.nombre}» agregado. Ya se puede elegir en la evaluación.`); }}
      />
      <ModalPredeterminado
        abierto={cambiandoPredeterminado}
        algoritmos={algoritmos.filter((a) => a.activo)}
        actual={codigoPredeterminado}
        onCerrar={() => setCambiandoPredeterminado(false)}
        onGuardado={() => { setCambiandoPredeterminado(false); guardado('Algoritmo preseleccionado actualizado. Las evaluaciones ya guardadas no cambian.'); }}
      />
      <ModalCriterio
        abierto={editandoCriterio}
        criterios={criterios}
        onCerrar={() => setEditandoCriterio(false)}
        onGuardado={() => { setEditandoCriterio(false); guardado('Criterio de gravedad actualizado. Las decisiones ya tomadas no se reclasifican.'); }}
      />
      <ModalPlazoInterno
        abierto={editandoPlazo}
        actual={horasDelPlazo}
        onCerrar={() => setEditandoPlazo(false)}
        onGuardado={(horas) => { setEditandoPlazo(false); guardado(`Plazo interno del Comité actualizado a ${horas} h hábiles. Los FAR-03 ya elaborados conservan el suyo.`); }}
      />
      <ModalEstadoDeAlgoritmo
        abierto={algoritmoDelEstado !== null}
        algoritmo={algoritmoDelEstado}
        onCerrar={() => setCodigoDelEstado(null)}
        onGuardado={(a, estado) => {
          setCodigoDelEstado(null);
          guardado(`Estado del Comité de «${a.nombre} · v${a.version}» actualizado a ${ESTADOS_DE_ALGORITMO[estado] ?? estado}.`);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Modales
// ---------------------------------------------------------------------------

function ModalAgregarAlgoritmo({ abierto, onCerrar, onGuardado }: {
  abierto: boolean; onCerrar: () => void; onGuardado: (a: Algoritmo) => void;
}) {
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [version, setVersion] = useState('1');
  const [nota, setNota] = useState('');
  const [problemas, setProblemas] = useState<Record<string, string>>({});
  const [error, setError] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => { setCodigo(''); setNombre(''); setVersion('1'); setNota(''); setProblemas({}); setError(null); };

  const guardar = async () => {
    const p: Record<string, string> = {};
    if (!/^[a-z][a-z0-9_]{1,59}$/.test(codigo)) p.codigo = 'Minúsculas, dígitos y guion bajo, empezando por letra. Es la clave que citan las evaluaciones.';
    if (!nombre.trim()) p.nombre = 'El nombre es lo que ve el Comité en el desplegable.';
    if (!version.trim()) p.version = 'La versión es lo que cada evaluación copia al clasificar.';
    setProblemas(p);
    if (Object.keys(p).length) return;
    setGuardando(true); setError(null);
    try {
      const creado = await api.agregarAlgoritmo({ codigo, nombre: nombre.trim(), version: version.trim(), nota: nota.trim() || null });
      limpiar();
      onGuardado(creado);
    } catch (e) {
      setError(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={abierto}
      onClose={() => { limpiar(); onCerrar(); }}
      sucio={Boolean(codigo || nombre || nota)}
      eyebrow="Administración · Reglas de negocio"
      title="Agregar un algoritmo de causalidad"
      footer={
        <>
          <Button variant="ghost" onClick={() => { limpiar(); onCerrar(); }}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando}>Agregar al catálogo</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert tone="info">
          Entra como <strong>propuesto</strong>: el Comité puede elegirlo, pero no se impone.
        </Alert>
        {error && <Alert tone="danger" title="No se pudo agregar" detalleTecnico={error.detalleTecnico}>{error.message}</Alert>}
        <Field label="Código" required hint="Minúsculas y guion bajo, p. ej. karch_lasagna. No se reutiliza nunca." error={problemas.codigo}>
          <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} />
        </Field>
        <Field label="Nombre" required hint="Como lo verá el Comité" error={problemas.nombre}>
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </Field>
        <Field label="Versión" required hint="La calibración. Cada evaluación la copia al clasificar." error={problemas.version}>
          <Input value={version} onChange={(e) => setVersion(e.target.value)} />
        </Field>
        <Field label="Nota" hint="Opcional. Fuente, observaciones del Comité.">
          <Textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} />
        </Field>
      </div>
    </Modal>
  );
}

function ModalPredeterminado({ abierto, algoritmos, actual, onCerrar, onGuardado }: {
  abierto: boolean; algoritmos: Algoritmo[]; actual: string; onCerrar: () => void; onGuardado: () => void;
}) {
  const [elegido, setElegido] = useState(actual);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Se reinicia al ABRIR (y solo entonces): una recarga tardía de `actual` mientras el
  // administrador ya eligió otro no debe pisarle la elección.
  const actualAlAbrir = useRef(actual);
  actualAlAbrir.current = actual;
  useEffect(() => { if (abierto) { setElegido(actualAlAbrir.current); setMotivo(''); setError(null); } }, [abierto]);

  const guardar = async () => {
    if (!elegido) { setError({ message: 'Elige el algoritmo que llegará preseleccionado.' }); return; }
    if (!motivo.trim()) { setError({ message: 'El motivo es obligatorio: es lo que explica en una inspección por qué cambió la regla.' }); return; }
    setGuardando(true); setError(null);
    try {
      await api.cambiarParametro(CLAVE_PREDETERMINADO, elegido, motivo.trim());
      setMotivo('');
      onGuardado();
    } catch (e) {
      setError(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={abierto}
      onClose={onCerrar}
      sucio={elegido !== actual || Boolean(motivo)}
      eyebrow="Administración · Reglas de negocio"
      title="Cambiar el algoritmo preseleccionado"
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || elegido === actual}>Guardar</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="danger" detalleTecnico={error.detalleTecnico}>{error.message}</Alert>}
        <Field label="Nuevo algoritmo preseleccionado" hint="El que llegará marcado en cada evaluación nueva. Se puede cambiar en cada una.">
          <Select
            sinVacio
            value={elegido}
            onChange={(e) => setElegido(String(e.target.value ?? ''))}
            options={algoritmos.map((a) => ({ value: a.codigo, label: `${a.nombre} · v${a.version}` }))}
          />
        </Field>
        <Alert tone="warning">
          Las evaluaciones ya guardadas <strong>conservan su algoritmo</strong>. Este cambio solo afecta a las que se abran a partir de ahora.
        </Alert>
        <Field label="Motivo del cambio" required hint="Queda en el historial y en la auditoría, con quién y cuándo">
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} />
        </Field>
      </div>
    </Modal>
  );
}

function ModalCriterio({ abierto, criterios, onCerrar, onGuardado }: {
  abierto: boolean; criterios: string[]; onCerrar: () => void; onGuardado: () => void;
}) {
  const [lista, setLista] = useState<string[]>(criterios);
  const [nuevo, setNuevo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Solo al ABRIR (HU-31-C4): una recarga tardía del criterio —la de un guardado
  // anterior que llega tarde— no debe borrar lo que se está editando.
  const criteriosAlAbrir = useRef(criterios);
  criteriosAlAbrir.current = criterios;
  useEffect(() => { if (abierto) { setLista(criteriosAlAbrir.current); setNuevo(''); setMotivo(''); setError(null); } }, [abierto]);

  const agregar = () => {
    const clave = aClaveDeCriterio(nuevo);
    if (!clave) return;
    if (lista.includes(clave)) { setError({ message: `«${etiquetaDelCriterio(clave)}» ya está en el criterio.` }); return; }
    setLista([...lista, clave]);
    setNuevo('');
    setError(null);
  };

  const guardar = async () => {
    if (!motivo.trim()) { setError({ message: 'El motivo es obligatorio.' }); return; }
    if (lista.length === 0) { setError({ message: 'El criterio no puede quedar vacío: sin condiciones, ningún evento podría ser grave.' }); return; }
    setGuardando(true); setError(null);
    try {
      await api.cambiarParametro(CLAVE_CRITERIO_GRAVEDAD, lista, motivo.trim());
      setMotivo('');
      onGuardado();
    } catch (e) {
      setError(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  const cambio = JSON.stringify(lista) !== JSON.stringify(criterios);

  return (
    <Modal
      open={abierto}
      onClose={onCerrar}
      sucio={cambio || Boolean(motivo)}
      eyebrow="Administración · Reglas de negocio"
      title="Ajustar el criterio de gravedad"
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !cambio}>Guardar</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="danger" detalleTecnico={error.detalleTecnico}>{error.message}</Alert>}
        <Field label="Condiciones vigentes" hint="Es grave el evento que cumple al menos una. Quita una con ×." grupo>
          <ul className="space-y-1">
            {lista.map((c) => (
              <li key={c} className="flex items-center justify-between text-body">
                <span>{etiquetaDelCriterio(c)}</span>
                <Button size="sm" variant="ghost" aria-label={`Quitar ${c}`} onClick={() => setLista(lista.filter((x) => x !== c))}>×</Button>
              </li>
            ))}
          </ul>
        </Field>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Agregar condición" hint='Se guarda como clave: "malformación congénita" → malformacion_congenita'>
              <Input value={nuevo} onChange={(e) => setNuevo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }} />
            </Field>
          </div>
          <Button variant="secondary" onClick={agregar}>Agregar</Button>
        </div>
        <Alert tone="warning">
          Las decisiones ya tomadas <strong>no se reclasifican</strong>: cada una conserva registrados los criterios con los que se decidió.
        </Alert>
        <Field label="Motivo del cambio" required hint="Queda en el historial y en la auditoría">
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} />
        </Field>
      </div>
    </Modal>
  );
}

function ModalPlazoInterno({ abierto, actual, onCerrar, onGuardado }: {
  abierto: boolean; actual: number | null; onCerrar: () => void; onGuardado: (horas: number) => void;
}) {
  const [horas, setHoras] = useState(actual === null ? '' : String(actual));
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Igual que los otros modales: se reinicia al ABRIR, no en cada recarga del padre.
  const actualAlAbrir = useRef(actual);
  actualAlAbrir.current = actual;
  useEffect(() => {
    if (abierto) { setHoras(actualAlAbrir.current === null ? '' : String(actualAlAbrir.current)); setMotivo(''); setError(null); }
  }, [abierto]);

  const numero = Number(horas);
  const valido = /^\d+$/.test(horas.trim()) && numero >= 1 && numero <= 720;
  const cambio = valido && numero !== actual;

  const guardar = async () => {
    if (!valido) { setError({ message: 'Escribe un número entero de horas hábiles (entre 1 y 720).' }); return; }
    if (!motivo.trim()) { setError({ message: 'El motivo es obligatorio: es lo que explica en una inspección por qué cambió el plazo.' }); return; }
    setGuardando(true); setError(null);
    try {
      await api.cambiarParametro(CLAVE_PLAZO_INTERNO, numero, motivo.trim());
      setMotivo('');
      onGuardado(numero);
    } catch (e) {
      setError(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={abierto}
      onClose={onCerrar}
      sucio={cambio || Boolean(motivo)}
      eyebrow="Administración · Reglas de negocio"
      title="Cambiar el plazo interno del Comité"
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !cambio}>Guardar</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="danger" detalleTecnico={error.detalleTecnico}>{error.message}</Alert>}
        <Field label="Horas hábiles para el FAR-03" required hint="Entero; el procedimiento vigente fija 24. Se cuentan solo horas de días hábiles del país del caso.">
          <Input type="number" min={1} max={720} step={1} inputMode="numeric" value={horas} onChange={(e) => setHoras(e.target.value)} />
        </Field>
        <Alert tone="warning">
          La bandeja del Comité usa el valor nuevo desde el siguiente refresco. Los FAR-03 ya elaborados <strong>conservan el plazo</strong> con el que se midieron.
        </Alert>
        <Field label="Motivo del cambio" required hint="Queda en el historial y en la auditoría, con quién y cuándo">
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} />
        </Field>
      </div>
    </Modal>
  );
}

function ModalEstadoDeAlgoritmo({ abierto, algoritmo, onCerrar, onGuardado }: {
  abierto: boolean; algoritmo: Algoritmo | null; onCerrar: () => void; onGuardado: (algoritmo: Algoritmo, estado: string) => void;
}) {
  // `actual` se lee de la fila VIVA del catálogo: si una recarga la cambia con el modal
  // abierto, «Guardar» se deshabilita solo cuando lo elegido ya es lo que hay.
  const actual = algoritmo?.estado ?? '';
  // Nombre y versión, como en el preseleccionado: el nombre solo no distingue Naranjo v1 de v2.
  const etiqueta = algoritmo ? `${algoritmo.nombre} · v${algoritmo.version}` : '';
  const [elegido, setElegido] = useState(actual);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Se reinicia al ABRIR (y solo entonces), como los otros modales: una recarga tardía del
  // catálogo mientras el administrador ya eligió otro estado no debe pisarle la elección.
  const actualAlAbrir = useRef(actual);
  actualAlAbrir.current = actual;
  useEffect(() => { if (abierto) { setElegido(actualAlAbrir.current); setMotivo(''); setError(null); } }, [abierto]);

  const guardar = async () => {
    if (!algoritmo) return;
    if (!motivo.trim()) { setError({ message: 'El motivo es obligatorio: es lo que explica en una inspección por qué el Comité cambió su parecer sobre el algoritmo.' }); return; }
    setGuardando(true); setError(null);
    try {
      await api.cambiarEstadoDeAlgoritmo(algoritmo.codigo, elegido, motivo.trim());
      setMotivo('');
      onGuardado(algoritmo, elegido);
    } catch (e) {
      setError(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={abierto}
      onClose={onCerrar}
      sucio={elegido !== actual || Boolean(motivo)}
      eyebrow="Administración · Reglas de negocio"
      title={`Estado del Comité · ${etiqueta}`}
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || elegido === actual}>Guardar</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="danger" title="No se pudo cambiar el estado" detalleTecnico={error.detalleTecnico}>{error.message}</Alert>}
        <Field label="Nuevo estado" hint="En qué punto está el Comité con este algoritmo. Es lo que muestra el catálogo.">
          <Select
            sinVacio
            value={elegido}
            onChange={(e) => setElegido(String(e.target.value ?? ''))}
            options={ESTADOS_DEL_COMITE}
          />
        </Field>
        <Alert tone="info">
          Es información del Comité: no cambia qué algoritmo se usa (eso lo decide el preseleccionado) ni retira el algoritmo.
        </Alert>
        <Field label="Motivo del cambio" required hint="Queda en el historial y en la auditoría, con quién y cuándo">
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} />
        </Field>
      </div>
    </Modal>
  );
}
